import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ConnectionError from "../../components/ConnectionError/ConnectionError";
import EmptyState from "../../components/EmptyState/EmptyState";
import { Pagination } from "../../components/common/Pagination/Pagination";
import ProductCard from "../../components/ProductCard/ProductCard";
import { publicContentService } from "../../services/publicContentService";
import { productService } from "../../services/productService";
import { resolveApiUrl } from "../../services/api";
import useReveal from "../../hooks/useReveal";
import type { ProductItem, ServiceItem } from "../../types";
import "./ServiceDetail.css";

const PAGE_SIZE = 12;
type SortOption = "default" | "nameAsc" | "nameDesc";

function ServiceDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const isArabic = i18n.resolvedLanguage?.startsWith("ar") ?? false;

  // URL-synced state (page is 1-based in the URL, 0-based for the API)
  const urlSearch = searchParams.get("search") || "";
  const urlPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  const [searchInput, setSearchInput] = useState(urlSearch);
  const [sort, setSort] = useState<SortOption>("default");

  const [service, setService] = useState<ServiceItem | null>(null);
  const [serviceLoading, setServiceLoading] = useState(true);

  const [products, setProducts] = useState<ProductItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Load the services list once and resolve the current service by id
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setServiceLoading(true);
      try {
        const services = await publicContentService.getServices();
        if (!cancelled) {
          setService(services.find((s) => s.id === id) || null);
        }
      } catch {
        if (!cancelled) setService(null);
      } finally {
        if (!cancelled) setServiceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Debounce the search box into the URL (500ms), resetting to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === urlSearch) return;
      const next = new URLSearchParams(searchParams);
      if (searchInput) next.set("search", searchInput);
      else next.delete("search");
      next.delete("page");
      setSearchParams(next, { replace: true });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadProducts = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(false);
    try {
      const response = await productService.getProducts({
        search: urlSearch,
        serviceId: id,
        page: urlPage - 1,
        size: PAGE_SIZE,
      });
      setProducts(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id, urlSearch, urlPage]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useReveal([products.length, loading, serviceLoading]);

  const handlePageChange = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page <= 0) next.delete("page");
    else next.set("page", String(page + 1));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Client-side ordering of the current page (backend has no sort param)
  const sortedProducts = useMemo(() => {
    if (sort === "default") return products;
    const nameOf = (p: ProductItem) => (isArabic ? p.nameAr : p.nameEn) || "";
    const sorted = [...products].sort((a, b) =>
      nameOf(a).localeCompare(nameOf(b), isArabic ? "ar" : "en")
    );
    return sort === "nameDesc" ? sorted.reverse() : sorted;
  }, [products, sort, isArabic]);

  if (!serviceLoading && !service) {
    return (
      <main className="page-main">
        <section className="section">
          <div className="container">
            <EmptyState
              title={t("products.serviceNotFound")}
              description={t("products.serviceNotFoundDesc")}
            />
            <div className="catalog-notfound-action">
              <Link to="/services" className="btn btn-outline">
                {t("products.backToServices")}
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const serviceTitle = service ? (isArabic ? service.titleAr : service.titleEn) : "";
  const serviceDesc = service ? (isArabic ? service.descAr : service.descEn) : "";

  return (
    <main className="page-main">
      <section className="section catalog-section">
        <div className="container">
          {/* Breadcrumb */}
          <nav className="catalog-breadcrumb reveal" aria-label="Breadcrumb">
            <Link to="/services">{t("products.backToServices")}</Link>
            <span className="catalog-breadcrumb-sep">/</span>
            <span className="catalog-breadcrumb-current">{serviceTitle}</span>
          </nav>

          {/* Hero / banner with service photo */}
          {service?.imageUrl ? (
            <div className="catalog-hero reveal">
              <img
                className="catalog-hero__img"
                src={resolveApiUrl(service.imageUrl)}
                alt={serviceTitle}
              />
              <div className="catalog-hero__overlay">
                <h1 className="catalog-hero__title">{serviceTitle}</h1>
                {serviceDesc && <p className="catalog-hero__desc">{serviceDesc}</p>}
              </div>
            </div>
          ) : (
            <>
              <h1 className="section-title reveal">{serviceTitle}</h1>
              {serviceDesc && <p className="services-subtitle reveal">{serviceDesc}</p>}
              <div className="services-divider"></div>
            </>
          )}

          {/* Toolbar: search + sort + count */}
          <div className="catalog-toolbar reveal">
            <div className="catalog-search-field">
              <span className="catalog-search-field__icon">
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                className="catalog-search-field__input"
                placeholder={t("products.searchPlaceholder")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                autoComplete="off"
              />
              {searchInput && (
                <button
                  className="catalog-search-field__clear"
                  onClick={() => setSearchInput("")}
                  aria-label="Clear search"
                  type="button"
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            <div className="catalog-toolbar-side">
              <span className="catalog-count">
                {t("products.resultsCount", { count: totalElements })}
              </span>
              <select
                className="catalog-sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortOption)}
                aria-label={t("products.sortBy")}
              >
                <option value="default">{t("products.sortDefault")}</option>
                <option value="nameAsc">{t("products.sortNameAsc")}</option>
                <option value="nameDesc">{t("products.sortNameDesc")}</option>
              </select>
            </div>
          </div>

          {/* Product grid */}
          {loading ? (
            <div className="catalog-grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="catalog-card catalog-card--skeleton">
                  <div className="catalog-skeleton catalog-skeleton--img" />
                  <div className="catalog-skeleton catalog-skeleton--line" />
                  <div className="catalog-skeleton catalog-skeleton--line short" />
                  <div className="catalog-skeleton catalog-skeleton--line shorter" />
                </div>
              ))}
            </div>
          ) : error ? (
            <ConnectionError onRetry={loadProducts} />
          ) : sortedProducts.length === 0 ? (
            <EmptyState
              icon={
                <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
                  <path d="M3.3 8.3L12 13l8.7-4.7" />
                  <path d="M12 13v9" />
                </svg>
              }
              title={t("products.noProducts")}
              description={t("products.noProductsDesc")}
            />
          ) : (
            <div className="catalog-grid">
              {sortedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <Pagination
            page={urlPage - 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            disabled={loading}
          />
        </div>
      </section>
    </main>
  );
}

export default ServiceDetail;
